/**
 * STEP — Utility Functions
 */
import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { baseSymbol } from "@/lib/currency";

/** shadcn/ui className merge utility */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Canonical money formatter for the whole app. Always use this (or `formatMoney`
 * below) instead of hand-rolling `toLocaleString() + " UZS"/" so'm"` so the label
 * and grouping stay identical on every screen.
 * Accepts numbers or numeric strings (API decimals come back as strings).
 */
export function formatCurrency(
  amount: number | string | null | undefined,
  currency = "UZS",
  locale = "uz-UZ"
): string {
  const parsed = typeof amount === "string" ? parseFloat(amount) : amount ?? 0;
  const value = Number.isFinite(parsed as number) ? (parsed as number) : 0;
  if (currency === "UZS") {
    // en-US gives comma thousands-separators (1,000,000) which is the
    // widely-recognised format in Uzbekistan and matches the rest of the app.
    // The `locale` param is intentionally ignored for UZS — it controls only
    // foreign-currency formatting below.
    return Math.round(value).toLocaleString("en-US") + " " + baseSymbol();
  }
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(value);
}

/** Shorthand for the common UZS / so'm case. */
export function formatMoney(amount: number | string | null | undefined): string {
  return formatCurrency(amount);
}

// ── Dates ──────────────────────────────────────────────────────────────────
// Uzbek month/weekday names are HARDCODED on purpose. `toLocale*String('uz-UZ',
// { month: 'short' | 'long' })` depends on the runtime's ICU locale data, which
// is missing/trimmed on many real devices (Android WebView, server Node without
// full-icu, some Safari builds). When it's missing, ICU silently falls back to
// the root locale and renders months as "M05" in yyyy-MM-dd order — the infamous
// "2026 M05 20". Formatting from raw date parts removes that dependency entirely.
const UZ_MONTHS = [
  "yanvar", "fevral", "mart", "aprel", "may", "iyun",
  "iyul", "avgust", "sentabr", "oktabr", "noyabr", "dekabr",
] as const;
const UZ_WEEKDAYS = [
  "yakshanba", "dushanba", "seshanba", "chorshanba", "payshanba", "juma", "shanba",
] as const;

function pad2(n: number): string {
  return n < 10 ? "0" + n : String(n);
}

/**
 * Uzbek textual date, e.g. "20-may, 2026". ICU-independent (see note above) so
 * it looks identical on every device. Use this instead of
 * `toLocaleDateString('uz-UZ', { month: 'short'|'long' })`.
 *   - weekday: prepend the weekday → "Chorshanba, 20-may, 2026"
 *   - year:    include the year (default true); pass false for compact rows → "20-may"
 *   - time:    append " HH:mm" → "20-may, 2026 14:30"
 */
export function formatDateUz(
  date: string | Date | number | null | undefined,
  opts: { weekday?: boolean; year?: boolean; time?: boolean } = {}
): string {
  if (date == null) return "";
  const d = new Date(date);
  if (isNaN(d.getTime())) return "";
  const { weekday = false, year = true, time = false } = opts;
  let out = `${d.getDate()}-${UZ_MONTHS[d.getMonth()]}`;
  if (year) out += `, ${d.getFullYear()}`;
  if (weekday) {
    const wd = UZ_WEEKDAYS[d.getDay()];
    out = `${wd.charAt(0).toUpperCase()}${wd.slice(1)}, ${out}`;
  }
  if (time) out += ` ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  return out;
}

/** Numeric date "DD.MM.YYYY" — deterministic, ICU-independent. */
export function formatDate(date: string | Date | null | undefined, _locale?: string): string {
  if (date == null) return "";
  const d = new Date(date);
  if (isNaN(d.getTime())) return "";
  return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()}`;
}

/** Numeric date + time "DD.MM.YYYY HH:mm" — deterministic, ICU-independent. */
export function formatDateTime(date: string | Date | null | undefined, _locale?: string): string {
  if (date == null) return "";
  const d = new Date(date);
  if (isNaN(d.getTime())) return "";
  return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/** Truncate long text with ellipsis */
export function truncate(str: string, maxLength: number): string {
  if (str.length <= maxLength) return str;
  return str.slice(0, maxLength) + "...";
}

/** Get user's initials for avatar fallback */
export function getInitials(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

/** Debounce function for search inputs */
export function debounce<T extends (...args: unknown[]) => void>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn(...args), delay);
  };
}

/** Check if a value is a valid UUID */
export function isValidUUID(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    value
  );
}

/** Calculate percentage change */
export function percentageChange(current: number, previous: number): number {
  if (previous === 0) return current > 0 ? 100 : 0;
  return ((current - previous) / previous) * 100;
}
